<script setup lang="ts">
  import { ColumnDefinition } from '@/shared/Table/models/ColumnDefinition'
  import { AplosFile } from '../models/AplosFile'
  import { AplosFileStatus } from '../models/AplosFileStatus'
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
  import type { Component } from 'vue'
  import Table from '@/shared/Table/Table.vue'
  import { HierarchicAplosFileViewModel } from '../models/HierarchicAplosFileViewModel'
  import { HierarchicAplosFileService } from '../services/HierarchicAplosFileService'
  import { Check, FileQuestionMark, RefreshCw } from 'lucide-vue-next'
  import Button from '@/components/ui/button/Button.vue'
  import { AplosFileApiService } from '../services/AplosFileApiService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { useUploadsStore } from '@/store/uploadsStore'
  import { getLocalizedDateTimeString, unixSecondsToDate } from '@/utils'
  import { Subject, takeUntil, throttleTime } from 'rxjs'
  import AppMenuBar from '@/components/Menu/AppMenuBar.vue'
  import AplosDropFileUpload from './AplosDropFileUpload.vue'
  import NumberOfFilesColumn from './NumberOfFilesColumn.vue'
  import FileNameColumn from './FileNameColumn.vue'
  import FileSizeColumn from './FileSizeColumn.vue'
  import FileUploadedColumn from './FileUploadedColumn.vue'
  import FileIdColumn from './FileIdColumn.vue'

  interface IFileManagerProps {
    // Optional static files array (for non-uploads use cases)
    files?: AplosFile[]
    // UI configuration
    title?: string
    description?: string
    showHeader?: boolean
    showUpload?: boolean
    showMenuBar?: boolean
    showSearch?: boolean
    initialSearch?: string
    emptyStateText?: string
    customActionColumn?: ColumnDefinition<HierarchicAplosFileViewModel>
    enablePagination?: boolean
    pageSize?: number
    autoRefreshInterval?: number // Auto-refresh interval in milliseconds (0 = disabled)
    // Data source configuration
    useUploadsStore?: boolean // Whether to use uploads store for data fetching
    statusFilter?: AplosFileStatus // Status filter for file queries
    // Selection mode
    selectable?: boolean // Whether rows are clickable for selection
    // add throttle interval prop
    clickThrottleMs?: number
    fileActionsComponent?: Component
  }

  // Declare _all_ your slots (so TS knows them)
  const slots = defineSlots<{
    /** slot used in your header: `<slot name="storage-info" />` */
    'storage-info': () => void

    /** slot used in menu bar center: `<slot name="menu-center-content" />` */
    'menu-center-content': () => void

    /** empty state: `<slot name="empty-state">…</slot>` */
    'empty-state': () => void
  }>()

  const createColumnDefinitions = (
    customActionColumn?: ColumnDefinition<HierarchicAplosFileViewModel>
  ): Array<ColumnDefinition<HierarchicAplosFileViewModel>> => {
    const columns = new Array<ColumnDefinition<HierarchicAplosFileViewModel>>()

    const idColumn = new ColumnDefinition<HierarchicAplosFileViewModel>('id', 'id', 'ID')
    idColumn.cellComponent = FileIdColumn
    idColumn.cellProps = { selectable: props.selectable, onCellClick: handleCellClick }
    idColumn.width = 120
    columns.push(idColumn)

    const numFilesColumn = new ColumnDefinition<HierarchicAplosFileViewModel>(
      'number_files',
      'number_files',
      '# of Files'
    )
    numFilesColumn.cellComponent = NumberOfFilesColumn
    numFilesColumn.cellProps = { selectable: props.selectable, onCellClick: handleCellClick }
    numFilesColumn.width = 120
    numFilesColumn.align = 'center'
    columns.push(numFilesColumn)

    const fileNameColumn = new ColumnDefinition<HierarchicAplosFileViewModel>(
      'name',
      'name',
      'File'
    )
    fileNameColumn.align = 'left'
    fileNameColumn.width = 300
    fileNameColumn.cellComponent = FileNameColumn
    fileNameColumn.cellProps = { selectable: props.selectable, onCellClick: handleCellClick }
    columns.push(fileNameColumn)

    const fileSizeColumn = new ColumnDefinition<HierarchicAplosFileViewModel>(
      'size',
      'size',
      'Size'
    )
    fileSizeColumn.cellComponent = FileSizeColumn
    fileSizeColumn.cellProps = { selectable: props.selectable, onCellClick: handleCellClick }
    columns.push(fileSizeColumn)

    const dateUploadedColumn = new ColumnDefinition<HierarchicAplosFileViewModel>(
      'createdUtcTs',
      'createdUtcTs',
      'Date Uploaded'
    )
    dateUploadedColumn.cellComponent = FileUploadedColumn
    dateUploadedColumn.cellProps = { selectable: props.selectable, onCellClick: handleCellClick }
    columns.push(dateUploadedColumn)

    const actionColumn =
      customActionColumn ?? new ColumnDefinition<AplosFile>('operation', 'operation', 'Action')
    actionColumn.width = 100
    columns.push(actionColumn)

    return columns
  }

  const props = withDefaults(defineProps<IFileManagerProps>(), {
    files: () => [],
    title: 'Files',
    description: 'available',
    showHeader: true,
    showUpload: true,
    showMenuBar: true,
    showSearch: true,
    emptyStateText: 'No Files',
    enablePagination: true,
    pageSize: 20,
    autoRefreshInterval: 0,
    useUploadsStore: true,
    selectable: false,
    // default throttle to 500ms
    clickThrottleMs: 500
  })

  const fileApiService: AplosFileApiService = InjectionService.injectStrict(
    AplosFileApiService.injectableKey
  )

  // Store initialization (only if using uploads store)
  const uploadStore = props.useUploadsStore ? useUploadsStore() : null

  const emit = defineEmits<{
    'file-selected': [selectedFile: AplosFile, rootFile: HierarchicAplosFileViewModel]
  }>()

  const isRefreshing = ref(false)
  const isManualRefresh = ref(false)
  const internalFiles = ref<AplosFile[]>([])
  const displayFiles = ref<HierarchicAplosFileViewModel[]>([])
  const lastRefreshTime = ref<Date | null>(null)
  const searchActive = ref(true)
  const searchQuery = ref(props.initialSearch ?? '')
  const refreshInterval = ref<NodeJS.Timeout | null>(null)

  // Animation state for removing files
  const deletingFileIds = ref<Set<string>>(new Set())

  // add rxjs Subjects for throttling and cleanup
  const clickSubject$ = new Subject<HierarchicAplosFileViewModel>()
  const destroy$ = new Subject<void>()

  const tableColumns = computed(() => {
    return createColumnDefinitions(props.customActionColumn)
  })

  const paginationConfig = computed(() => {
    if (!props.enablePagination) return false
    return {
      showSizeChanger: props.files.length >= props.pageSize,
      pageSize: props.pageSize
    }
  })

  const tableDataSource = computed(() => {
    if (isRefreshing.value) {
      return Array.from({ length: 5 }).map((_, i) => ({ __skeleton: true, id: `skeleton-${i}` }))
    }
    return displayFiles.value
  })

  // Handle cell click for selectable mode
  function handleCellClick(record: HierarchicAplosFileViewModel, event: Event) {
    if (props.selectable) {
      // Prevent clicking on buttons from triggering selection
      const target = event.target as HTMLElement
      if (target.closest('button') || target.closest('.ant-btn')) {
        return
      }

      // throttle clicks per ms
      clickSubject$.next(record)
    }
  }

  function onFileSelected(record: HierarchicAplosFileViewModel) {
    const rootFile = HierarchicAplosFileService.findRootForFile(record, displayFiles.value)
    emit('file-selected', record as unknown as AplosFile, rootFile)
  }

  function getRowClassName(record: HierarchicAplosFileViewModel) {
    const classes = []
    if (deletingFileIds.value.has(record.id ?? '')) {
      classes.push('row-delete-anim')
    }
    if (props.selectable) {
      classes.push('selectable-row')
    }
    if (record.hasMatchedSearch) {
      classes.push('matched-search')
    }
    return classes.join(' ')
  }

  const lastRefreshMessage = computed(() => {
    if (lastRefreshTime.value) {
      return `Last Refresh: ${getLocalizedDateTimeString(lastRefreshTime.value.toISOString())}`
    }
    return ''
  })

  const menuButtons = computed(() => {
    const buttons = []

    // Always show refresh button if we have uploads store or static files
    if (props.useUploadsStore || props.files?.length) {
      // Create a new button object each time to ensure reactivity
      buttons.push({
        id: 'refresh-files-button',
        icon: RefreshCw,
        onClick: handleRefresh,
        animate: isRefreshing.value, // This should trigger reactivity
        title: '',
        toolTip: 'Refresh Files',
        // Add a key that changes to force re-render
        key: `refresh-${isRefreshing.value ? 'spinning' : 'idle'}`
      })
    }

    return buttons
  })

  const menuMessages = computed(() => {
    const messages = []
    if (lastRefreshMessage.value) {
      messages.push(lastRefreshMessage.value)
    }
    return messages
  })

  // Helper function to compare file arrays
  const areFilesEqual = (files1: AplosFile[], files2: AplosFile[]): boolean => {
    if (files1.length !== files2.length) return false

    // Compare each file by id and key properties
    for (let i = 0; i < files1.length; i++) {
      const file1 = files1[i]
      const file2 = files2[i]

      if (
        file1.id !== file2.id ||
        file1.name !== file2.name ||
        file1.size !== file2.size ||
        file1.createdUtcTs !== file2.createdUtcTs ||
        file1.state !== file2.state
      ) {
        return false
      }
    }

    return true
  }

  // Auto-refresh setup
  const setupAutoRefresh = () => {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
    }

    if (props.autoRefreshInterval > 0) {
      refreshInterval.value = setInterval(() => {
        loadFiles(false) // Pass false to indicate auto-refresh
      }, props.autoRefreshInterval)
    }
  }

  // Internal data management methods
  const loadFiles = async (manualRefresh = false) => {
    isManualRefresh.value = manualRefresh

    if (props.useUploadsStore && uploadStore) {
      try {
        if (manualRefresh) {
          isRefreshing.value = true
        }

        await uploadStore.loadData(fileApiService, props.statusFilter)
        const newFiles = uploadStore.uploads.files

        // Only update if data has actually changed
        if (!areFilesEqual(internalFiles.value, newFiles)) {
          if (!manualRefresh) {
            isRefreshing.value = true
          }
          internalFiles.value = newFiles
          lastRefreshTime.value = new Date(uploadStore.uploads.lastFetch)
        } else {
          // Data is the same, just update the refresh time (no loading state)
          lastRefreshTime.value = new Date(uploadStore.uploads.lastFetch)
        }
        applySearch() // Apply current search after loading
      } catch (error) {
        console.error('Failed to fetch files:', error)
      } finally {
        isRefreshing.value = false
      }
    } else {
      // Use static files from props
      const newFiles = props.files || []
      if (!areFilesEqual(internalFiles.value, newFiles)) {
        if (manualRefresh) {
          isRefreshing.value = true
        }
        internalFiles.value = newFiles
        applySearch()
        isRefreshing.value = false
      }
    }
  }

  const applySearch = () => {
    const currentFiles = internalFiles.value

    if (searchActive.value && searchQuery.value) {
      const searchValue = searchQuery.value.toLowerCase()
      const filteredFiles = currentFiles.filter((file) => {
        const fileName = file.name ? file.name.toLowerCase() : ''
        const userId = file.userId ? file.userId.toLowerCase() : ''
        const fileId = file.id ? file.id.toLowerCase() : ''
        let uploadedDate = ''

        if (file.createdUtcTs) {
          uploadedDate = unixSecondsToDate(file.createdUtcTs)?.toLocaleDateString() ?? ''
        }

        const fieldsToCheck = [fileName, uploadedDate, userId, fileId]
        return fieldsToCheck.some((field) => field.includes(searchValue))
      })

      // Build a hierarchical view from the filtered flat list, using the full currentFiles as source.
      displayFiles.value = HierarchicAplosFileService.buildHierarchicArrayFromFiltered(
        filteredFiles,
        currentFiles
      )
    } else {
      displayFiles.value = HierarchicAplosFileService.buildHierarchicArray(currentFiles)
    }
  }

  const handleRefresh = async () => {
    await loadFiles(true) // Pass true to indicate manual refresh
  }

  const handleSearch = (value: string) => {
    searchQuery.value = value
    searchActive.value = value.length > 0
    applySearch()
  }

  // Remove file(s) by id from lists, with animation
  function removeFiles(fileIds: Array<string>) {
    fileIds.map((x) => deletingFileIds.value.add(x))

    setTimeout(() => {
      internalFiles.value = internalFiles.value.filter(
        (file) => file.id != null && !fileIds.includes(file.id)
      )
      // reset filter from updated (item removed) from internalFiles and rebuild hierarhic model
      applySearch()
      fileIds.forEach((id) => deletingFileIds.value.delete(id))
    }, 600) // Animation duration
  }

  // Watchers
  watch(
    () => props.files,
    () => {
      // Reload internal files when props.files changes (for static file mode)
      if (!props.useUploadsStore) {
        loadFiles()
      }
    },
    { deep: true }
  )

  watch(
    () => props.statusFilter,
    () => {
      handleRefresh()
    }
  )

  watch(
    () => props.autoRefreshInterval,
    () => {
      setupAutoRefresh()
    }
  )

  const onAfterUpload = async () => {
    await loadFiles(true)
  }

  defineExpose({
    handleRefresh,
    removeFiles,
    displayFiles
  })

  // Lifecycle
  onMounted(async () => {
    // First, populate with cached data if available (for immediate display)
    if (props.useUploadsStore && uploadStore) {
      const cachedFiles = uploadStore.uploads.files
      if (cachedFiles && cachedFiles.length > 0) {
        internalFiles.value = cachedFiles
        lastRefreshTime.value = uploadStore.uploads.lastFetch
          ? new Date(uploadStore.uploads.lastFetch)
          : null
        //applySearch() // Apply any existing search
      }
    } else {
      // For static files mode, use props immediately
      internalFiles.value = props.files || []
      //applySearch()
    }

    // Then fetch fresh data in the background
    await loadFiles(true)

    // subscribe to throttled clicks
    clickSubject$
      .pipe(throttleTime(props.clickThrottleMs), takeUntil(destroy$))
      .subscribe((record) => {
        onFileSelected(record)
      })

    setupAutoRefresh()
  })

  onUnmounted(() => {
    if (refreshInterval.value) {
      clearInterval(refreshInterval.value)
    }

    // cleanup rxjs subscriptions
    destroy$.next()
    destroy$.complete()
  })
</script>

<template>
  <div
    class="file-manager rounded-lg border border-gray-200 p-4 shadow dark:border-zinc-800 dark:bg-[#0d1526] dark:text-gray-200"
  >
    <!-- Header Section -->
    <div v-if="showHeader" class="mb-6">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-semibold">{{ title }}</h3>
          <p class="mt-1 text-sm">
            {{ files.length }} {{ files.length === 1 ? 'file' : 'files' }} {{ description }}
          </p>
        </div>
        <div v-if="showUpload" class="flex items-center gap-4">
          <!-- Storage Gauge (if provided) -->
          <slot name="storage-info" />
          <!-- File Upload -->
          <AplosDropFileUpload @after-upload="onAfterUpload" />
        </div>
      </div>
    </div>

    <!-- Menu Bar -->
    <div v-if="showMenuBar" class="mb-4">
      <AppMenuBar
        :key="`menu-${isRefreshing ? 'refreshing' : 'idle'}`"
        :showSearch="showSearch"
        :onSearch="handleSearch"
        :buttons="menuButtons"
        :messages="menuMessages"
        :searchValue="searchQuery"
      >
        <template #center-content>
          <slot name="menu-center-content" />
        </template>
      </AppMenuBar>
    </div>

    <!-- File Table -->
    <Table
      :data-source="tableDataSource"
      :column-definitions="tableColumns"
      @row-selected="handleCellClick"
      :pagination-config="paginationConfig"
      :loading="isRefreshing"
      :rowClassName="getRowClassName"
      :emitRowSelection="false"
      :class="['ant-table-striped ant-table-hover overflow-hidden rounded-lg']"
      :scroll="tableDataSource && tableDataSource.length ? { x: true } : undefined"
    >
      <!-- ant-design-vue table generates slots based on column key (columnId) -->
      <template #cell-operation="{ record }">
        <template v-if="props.fileActionsComponent == null">
          <a-tooltip title="Select File">
            <Button
              size="icon"
              :disabled="HierarchicAplosFileService.getFileErrorsForManager(record)?.length"
              variant="secondary"
              @click="onFileSelected(record)"
            >
              <Check class="size-5" />
            </Button>
          </a-tooltip>
        </template>
        <template v-else>
          <component :is="props.fileActionsComponent" :record="record" />
        </template>
      </template>

      <template #emptyText>
        <div class="py-10">
          <slot v-if="!isRefreshing" name="empty-state">
            <FileQuestionMark class="mx-auto mb-5 size-14 text-muted-foreground" />
            <div class="text-muted-foreground">{{ emptyStateText }}</div>
          </slot>
        </div>
      </template>
    </Table>
  </div>
</template>

<style scoped>
  .file-manager {
    width: 100%;
  }
</style>
